import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CTA_IO,
  STUDIO_TAB_META,
  tabStateFromPipeline,
  type CtaIoKey,
  type StudioTab,
} from "@/lib/carouselStudioMeta";
import type { CarouselItem, CarouselPipeline, CarouselSourceHit } from "@/lib/api";
import { cn } from "@/lib/utils";

export function TabStateDot({ state }: { state?: string }) {
  return (
    <span
      className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        state === "done" && "bg-emerald-600",
        state === "active" && "animate-pulse bg-brand",
        state === "error" && "bg-destructive",
        state === "blocked" && "bg-muted-foreground/40",
        (!state || state === "waiting") && "bg-slate-300",
      )}
      aria-hidden
    />
  );
}

export function StudioTabsList({
  pipeline,
  item,
  kind,
}: {
  pipeline?: CarouselPipeline | null;
  item?: CarouselItem | null;
  kind?: string;
}) {
  const pendingWyniki = (item?.lessons || []).filter(
    (l) => l.status === "pending" && (l as { source?: string }).source === "results",
  ).length;
  const discussionRetroId = String(
    (item as { retro_discussion?: { id?: string } } | null | undefined)?.retro_discussion?.id ||
      ((item as { retro?: { id?: string; phase?: string } } | null | undefined)?.retro?.phase === "discussion"
        ? (item as { retro?: { id?: string } }).retro?.id
        : "") ||
      "",
  );
  const pendingRetro = (item?.lessons || []).filter((l) => {
    if (l.status !== "pending") return false;
    if (discussionRetroId) return String((l as { retro_id?: string }).retro_id || "") === discussionRetroId;
    const src = (l as { source?: string }).source || "";
    return src === "discussion" || !src;
  }).length;
  const pubKind = kind || item?.kind || "";
  const tabs = STUDIO_TAB_META.filter((tab) => {
    if (tab.id === "draft" && pubKind && pubKind !== "carousel") return false;
    if (tab.id === "generator" && pubKind && pubKind !== "carousel") return false;
    return true;
  });
  const isCzasotekst = pubKind === "czasotekst";
  return (
    <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1 lg:w-auto">
      {tabs.map((tab) => {
        const state = tabStateFromPipeline(pipeline, tab.stageIds);
        const label =
          isCzasotekst && tab.id === "copy"
            ? "Treść"
            : isCzasotekst && tab.id === "render"
              ? "Film i skład"
              : tab.label;
        const pendingBadge =
          tab.id === "retro" ? pendingRetro : tab.id === "wyniki" ? pendingWyniki : 0;
        return (
          <TabsTrigger key={tab.id} value={tab.id} className="min-h-8 gap-1.5 px-2.5 text-sm">
            <TabStateDot state={state} />
            {label}
            {pendingBadge > 0 ? (
              <span className="rounded-full bg-brand/15 px-1.5 text-[10px] font-semibold text-brand">
                {pendingBadge}
              </span>
            ) : null}
            <span className="sr-only">
              {state === "active" ? "w toku" : state === "error" ? "błąd" : state === "done" ? "gotowe" : "czeka"}
            </span>
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}

export function StudioProcessChain({
  pipeline,
}: {
  pipeline?: CarouselPipeline | null;
  tab?: StudioTab;
  onTab?: (next: StudioTab) => void;
}) {
  if (!pipeline?.now) return null;
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Status: {pipeline.now}
    </p>
  );
}

export function StudioSplit({
  left,
  right,
  wideRight = false,
}: {
  left: ReactNode;
  right: ReactNode;
  wideRight?: boolean;
}) {
  return (
    <div className="grid min-w-0 gap-5 xl:grid-cols-12">
      <div className={cn("min-w-0 space-y-5", wideRight ? "xl:col-span-5" : "xl:col-span-7")}>{left}</div>
      <aside className={cn("min-w-0 space-y-5", wideRight ? "xl:col-span-7" : "xl:col-span-5")}>{right}</aside>
    </div>
  );
}

export function CtaIo({ io }: { io: CtaIoKey }) {
  const row = CTA_IO[io];
  return (
    <p className="text-[11px] leading-snug text-muted-foreground">
      {row.take} {row.give}
    </p>
  );
}

export function AiActionButton({
  label,
  io,
  running,
  failed,
  disabled,
  variant = "brand",
  className,
  onClick,
  icon,
}: {
  label: string;
  io: CtaIoKey;
  running?: boolean;
  failed?: boolean;
  disabled?: boolean;
  variant?: "brand" | "outline";
  className?: string;
  onClick: () => void;
  icon?: ReactNode;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <Button
        type="button"
        variant={variant}
        className="w-full min-h-10"
        disabled={running ? false : disabled}
        onClick={onClick}
      >
        <span
          className={cn(
            "mr-2 h-2 w-2 shrink-0 rounded-full",
            running && "animate-pulse bg-brand",
            failed && !running && "bg-destructive",
            !running && !failed && "bg-transparent",
          )}
          aria-hidden
        />
        {icon}
        {running ? `${label} · w toku` : label}
      </Button>
      <CtaIo io={io} />
    </div>
  );
}

export function BriefPreview({
  track,
  painLabel,
  skuLabel,
  formatLabel,
  styleLabel,
  bgMode,
  funnel,
  ctaIntent,
  closeMode,
  note,
  sources,
}: {
  track: string;
  painLabel: string;
  skuLabel: string;
  formatLabel: string;
  styleLabel: string;
  bgMode: string;
  funnel: string;
  ctaIntent: string;
  closeMode: string;
  note: string;
  sources: CarouselSourceHit[];
}) {
  return (
    <Card className="border-slate-200 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="text-base text-slate-900">Co pójdzie do modelu</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          To skrót briefu. Przycisk AI otwiera okno z tymi parametrami i statusem.
        </p>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <PreviewRow label="Tor" value={track} />
          <PreviewRow label="Lejek" value={funnel.toUpperCase()} />
          <PreviewRow label="Ból" value={painLabel} />
          <PreviewRow label="SKU" value={skuLabel} />
          <PreviewRow label="Typ" value={formatLabel} />
          <PreviewRow label="Styl" value={styleLabel} />
          <PreviewRow label="Tło" value={bgMode === "gemini" ? "Gemini" : "druk"} />
          <PreviewRow label="CTA" value={`${ctaIntent} · ${closeMode}`} />
        </dl>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Notatka</div>
          <p className="mt-1 text-xs">{note.trim() || "brak"}</p>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Źródła ({sources.length})
          </div>
          {sources.length === 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">Brak trafień. Model dostanie pusty korpus.</p>
          ) : (
            <ul className="mt-2 max-h-64 space-y-2 overflow-auto">
              {sources.slice(0, 8).map((hit, i) => (
                <li key={`${hit.type}-${hit.id}-${i}`} className="rounded-md border border-border p-2 text-xs">
                  <div className="text-muted-foreground">{hit.meta || hit.kind || hit.type}</div>
                  <p className="mt-0.5">{hit.excerpt || hit.hook}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CopyOutcomePanel({
  item,
  selectedHook,
}: {
  item?: CarouselItem;
  selectedHook?: string;
}) {
  const slides = item?.copy?.slides || [];
  const review = item?.discussion?.copy_review;
  return (
    <Card className="border-slate-200 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="text-base text-slate-900">Co z czego</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Wybrana karta staje się slajdami. Recenzja ról ocenia gotowe ściany, nie koncepcję.
        </p>
        <p className="text-sm">
          <span className="text-muted-foreground">Karta: </span>
          {selectedHook || "jeszcze nie wybrana"}
        </p>
        <p className="text-sm">
          <span className="text-muted-foreground">Slajdy: </span>
          {slides.length ? `${slides.length} ścian` : "brak"}
        </p>
        {review?.verdict ? (
          <p className="text-sm">
            <span className="text-muted-foreground">Recenzja: </span>
            {review.verdict}
          </p>
        ) : null}
        {item?.discussion?.verdict ? (
          <p className="text-xs text-muted-foreground">{item.discussion.verdict}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value || "brak"}</dd>
    </div>
  );
}
